import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegulatoryUpdatesPanel = ({ updates = [] }) => {
  const [expandedUpdate, setExpandedUpdate] = useState(null);

  const toggleExpanded = (updateId) => {
    setExpandedUpdate(expandedUpdate === updateId ? null : updateId);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'important':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'informational':
        return 'text-primary bg-primary/10 border-primary/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return 'AlertTriangle';
      case 'important':
        return 'AlertCircle';
      case 'informational':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Bell" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Regulatory Updates</h3>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {updates?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <p className="text-muted-foreground">All regulatory updates reviewed</p>
          </div>
        ) : (
          <div className="space-y-1">
            {updates?.map((update) => (
              <div key={update?.id} className="border-b border-border last:border-b-0">
                <div className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`p-1.5 rounded-lg border ${getSeverityColor(update?.severity)}`}>
                      <Icon name={getSeverityIcon(update?.severity)} size={16} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground line-clamp-1">
                          {update?.title}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(update?.severity)}`}>
                          {update?.severity?.toUpperCase()}
                        </span>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-2">
                        {update?.source} • {update?.publishedAt}
                      </p>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {update?.summary}
                      </p>
                      
                      {expandedUpdate === update?.id && (
                        <div className="mb-3 p-3 bg-muted rounded-lg">
                          <p className="text-sm text-foreground whitespace-pre-line">
                            {update?.details}
                          </p>
                          {update?.affectedRules && update?.affectedRules?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-medium text-foreground mb-2">Affected Rules:</p>
                              <div className="flex flex-wrap gap-1">
                                {update?.affectedRules?.map((rule, index) => (
                                  <span key={index} className="px-2 py-1 bg-background rounded text-xs text-muted-foreground">
                                    {rule}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName={expandedUpdate === update?.id ? 'ChevronUp' : 'ChevronDown'}
                          iconSize={14}
                          onClick={() => toggleExpanded(update?.id)}
                        >
                          {expandedUpdate === update?.id ? 'Show Less' : 'Show More'}
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="ExternalLink"
                          iconSize={14}
                        >
                          View Full
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegulatoryUpdatesPanel;